import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const STATUS_CONFIG = {
  pending:     { label: 'Не начато',  next: 'in_progress' },
  in_progress: { label: 'В работе',   next: 'done' },
  done:        { label: 'Выполнено',  next: 'pending' },
};

// Вычисляет эффективный статус с учётом повторений
function getEffectiveStatus(task) {
  if (!task.is_recurring || !task.last_completed_at) return task.status;
  const completed = new Date(task.last_completed_at);
  const now = new Date();
  if (task.recurring_period === 'week') {
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay() + 1); // пн
    startOfWeek.setHours(0, 0, 0, 0);
    return completed >= startOfWeek ? 'done' : 'pending';
  }
  if (task.recurring_period === 'month') {
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return completed >= startOfMonth ? 'done' : 'pending';
  }
  return task.status;
}

// Метрики развития юридической компании (24.09.2026). Авто — считаются из CRM при выборе месяца.
const METRIC_LABELS = {
  leads:         { label: 'Обращений (авто)',        unit: '' },
  contracts:     { label: 'Договоров (авто)',        unit: '' },
  revenue:       { label: 'Выручка всего (авто)',    unit: '₽' },
  revenue_civil: { label: 'из неё гражданские (авто)', unit: '₽' },
  reviews_count: { label: 'Отзывов на Яндексе',      unit: '' },
  rating:        { label: 'Рейтинг на Яндексе',      unit: '' },
  ad_spend:      { label: 'Расходы на рекламу',      unit: '₽' },
  reserve:       { label: 'Резерв',                  unit: '₽' },
};

const TARGET_KEYS = {
  leads:         'target_leads',
  contracts:     'target_contracts',
  revenue:       'target_revenue',
  reviews_count: 'target_reviews',
  rating:        'target_rating',
  reserve:       'target_reserve',
};
const EMPTY_METRIC = { month: '', leads: '', contracts: '', revenue: '', revenue_civil: '', reviews_count: '', rating: '', ad_spend: '', reserve: '' };

function formatNum(val, unit) {
  if (val == null) return '—';
  const n = Number(val);
  if (unit === '₽') return n.toLocaleString('ru-RU') + ' ₽';
  return n.toLocaleString('ru-RU');
}

function DatePicker({ value, onSave, label }) {
  const [show, setShow] = React.useState(false);
  return (
    <span style={{position:'relative',display:'inline-flex',alignItems:'center',gap:4}}>
      <span>{label}</span>
      <button onClick={e=>{e.stopPropagation();setShow(v=>!v);}}
        style={{fontSize:12,padding:'1px 4px',border:'1px solid #d1d5db',borderRadius:4,background:'#f9fafb',cursor:'pointer',lineHeight:1}}>дата</button>
      {show && (
        <div style={{position:'absolute',top:'110%',left:0,zIndex:100,background:'#fff',border:'1px solid #e5e7eb',borderRadius:8,padding:10,boxShadow:'0 4px 16px rgba(0,0,0,0.12)',whiteSpace:'nowrap'}}>
          <div style={{fontSize:11,color:'#6b7280',marginBottom:6}}>Новая дата:</div>
          <input type="date" defaultValue={value} autoFocus
            onChange={e=>{if(e.target.value){onSave(e.target.value);setShow(false);}}}
            style={{padding:'5px 8px',borderRadius:6,border:'1px solid #d1d5db',fontSize:13}} />
          <button onClick={()=>setShow(false)}
            style={{display:'block',width:'100%',marginTop:6,fontSize:11,color:'#6b7280',background:'none',border:'none',cursor:'pointer'}}>Отмена</button>
        </div>
      )}
    </span>
  );
}

function PhaseCard({ phase, onTaskClick, onPhaseDate, onTaskDate, onRecurringChange }) {
  const tasks = phase.tasks || [];
  const done = tasks.filter(t => getEffectiveStatus(t) === 'done').length;
  const pct = tasks.length ? Math.round((done / tasks.length) * 100) : 0;

  const today = new Date();
  const start = new Date(phase.period_start);
  const end = new Date(phase.period_end);
  const isActive = today >= start && today <= end;

  return (
    <div style={{
      border: isActive ? '2px solid #2563eb' : '1px solid #e5e7eb',
      borderRadius: 12, padding: 20, marginBottom: 16,
      background: isActive ? '#eff6ff' : '#fff'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
            {isActive && <span style={{ color: '#2563eb' }}>● </span>}
            {phase.name}
          </h3>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2, display:'flex', gap:6, alignItems:'center' }}>
            <DatePicker value={phase.period_start} label={new Date(phase.period_start).toLocaleDateString('ru-RU')} onSave={d=>onPhaseDate(phase.id,'period_start',d)} />
            <span>—</span>
            <DatePicker value={phase.period_end} label={new Date(phase.period_end).toLocaleDateString('ru-RU')} onSave={d=>onPhaseDate(phase.id,'period_end',d)} />
          </div>
          <div style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>{phase.goal}</div>
        </div>
        <div style={{ textAlign: 'center', minWidth: 60 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: isActive ? '#2563eb' : '#374151' }}>{pct}%</div>
          <div style={{ fontSize: 11, color: '#9ca3af' }}>{done}/{tasks.length}</div>
        </div>
      </div>

      <div style={{ height: 6, background: '#e5e7eb', borderRadius: 3, margin: '12px 0' }}>
        <div style={{ height: 6, width: pct + '%', background: '#2563eb', borderRadius: 3, transition: 'width 0.3s' }} />
      </div>

      <div>
        {tasks.map(task => {
          const effStatus = getEffectiveStatus(task);
          return (
            <div key={task.id}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '8px 0', borderBottom: '1px solid #f3f4f6'
              }}>
              <span style={{ fontSize: 16, minWidth: 20, cursor: 'pointer' }}
                onClick={() => onTaskClick(task)}>
                {effStatus === 'done' ? '✓' : effStatus === 'in_progress' ? '◑' : '○'}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <span
                    onClick={() => onTaskClick(task)}
                    style={{
                      fontSize: 13, cursor: 'pointer',
                      textDecoration: effStatus === 'done' ? 'line-through' : 'none',
                      color: effStatus === 'done' ? '#9ca3af' : '#111827'
                    }}>{task.action}</span>
                  {task.is_recurring && (
                    <span style={{ fontSize: 11, padding:'1px 6px', borderRadius:4,
                      background: task.recurring_period==='week' ? '#eff6ff' : '#f0fdf4',
                      color: task.recurring_period==='week' ? '#2563eb' : '#16a34a',
                      border: `1px solid ${task.recurring_period==='week'?'#bfdbfe':'#86efac'}`,
                      whiteSpace:'nowrap'
                    }}>
                      {task.recurring_period==='week'?'еженед.':'ежемес.'}
                      {task.completed_count > 0 && ` · ${task.completed_count}×`}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', display:'flex', alignItems:'center', gap:6, marginTop:2 }}>
                  <DatePicker value={task.deadline} label={'До '+new Date(task.deadline).toLocaleDateString('ru-RU')} onSave={d=>{onTaskDate(task.id,d);}} />
                  · {task.result}
                  · <select
                    value={task.is_recurring ? (task.recurring_period || 'week') : ''}
                    onClick={e => e.stopPropagation()}
                    onChange={e => onRecurringChange(task.id, e.target.value)}
                    style={{ fontSize:11, border:'none', background:'transparent', color:'#6b7280', cursor:'pointer', padding:0 }}>
                    <option value="">разовая</option>
                    <option value="week">еженедельно</option>
                    <option value="month">ежемесячно</option>
                  </select>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function RoadmapPage() {
  const [phases, setPhases] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [targets, setTargets] = useState(null);
  const [showMetricForm, setShowMetricForm] = useState(false);
  const [metricForm, setMetricForm] = useState(EMPTY_METRIC);
  const [autofillLoading, setAutofillLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showTargetForm, setShowTargetForm] = useState(false);
  const [editingMetricId, setEditingMetricId] = useState(null);
  const [targetForm, setTargetForm] = useState({ target_leads:'', target_contracts:'', target_revenue:'', target_reviews:'', target_rating:'', target_reserve:'' });

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    const [p, m] = await Promise.all([
      api.get('/roadmap/phases').then(r => r.data),
      api.get('/roadmap/metrics').then(r => r.data),
    ]);
    setPhases(p);
    setMetrics(m.metrics || []);
    setTargets(m.targets);
    setLoading(false);
  }

  async function handleMonthChange(val) {
    const monthStr = val + '-01';
    setMetricForm(f => ({ ...f, month: monthStr }));
    if (!val) return;
    setAutofillLoading(true);
    try {
      const r = await api.get('/roadmap/metrics/autofill/' + val);
      setMetricForm(f => ({ ...f, month: monthStr, new_clients: r.data.new_clients, leads: r.data.leads, contracts: r.data.contracts, revenue: r.data.revenue, revenue_civil: r.data.revenue_civil, ...(r.data.ad_spend ? { ad_spend: r.data.ad_spend } : {}) }));
    } catch(e) {}
    setAutofillLoading(false);
  }

  async function handleTaskClick(task) {
    const effStatus = getEffectiveStatus(task);
    const next = STATUS_CONFIG[effStatus]?.next || "pending";
    await api.patch(`/roadmap/tasks/${task.id}`, { status: next });
    loadAll();
  }


  async function handlePhaseDate(phaseId, field, value) {
    await api.patch(`/roadmap/phases/${phaseId}`, { [field]: value });
    loadAll();
  }

  async function handleRoadmapTaskDate(taskId, value) {
    await api.patch(`/roadmap/tasks/${taskId}/date`, { deadline: value });
    loadAll();
  }

  async function handleRecurringChange(taskId, value) {
    await api.patch(`/roadmap/tasks/${taskId}`, {
      is_recurring: value !== '',
      recurring_period: value || null,
    });
    loadAll();
  }

  function openTargetForm() {
    setTargetForm(Object.fromEntries(Object.values(TARGET_KEYS).map(k => [k, targets?.[k] ?? ''])));
    setShowTargetForm(true);
  }

  async function handleTargetSubmit() {
    await api.patch('/roadmap/targets', targetForm);
    setShowTargetForm(false);
    loadAll();
  }

  async function handleMetricSubmit() {
    await api.post('/roadmap/metrics', metricForm);
    setShowMetricForm(false);
    setMetricForm(EMPTY_METRIC);
    loadAll();
  }

  async function deleteMetric(id) {
    if (!window.confirm('Удалить запись?')) return;
    await api.delete(`/roadmap/metrics/${id}`);
    loadAll();
  }

  function editMetric(m) {
    setMetricForm({
      ...Object.fromEntries(Object.keys(EMPTY_METRIC).map(k => [k, m[k] ?? ''])),
      month: m.month ? m.month.slice(0,7)+'-01' : '',
    });
    setShowMetricForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const lastMetric = metrics[metrics.length - 1] || null;

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Загрузка...</div>;

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Развитие</h1>
      <p style={{ color: '#6b7280', marginBottom: 24 }}>Юридический центр «Фемида» · План развития юридической компании в Белорецке · цель на сентябрь 2027</p>

      {/* Контрольная панель */}
      {lastMetric && (
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 12, padding: 20, marginBottom: 24 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#166534' }}>
              Прогресс · {new Date(lastMetric.month).toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })}
            </h2>
            <button onClick={openTargetForm} style={{ fontSize:12, padding:'5px 12px', borderRadius:6, border:'1px solid #86efac', background:'#fff', color:'#166534', cursor:'pointer', fontWeight:600 }}>
              Изменить цели
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
            {Object.entries(METRIC_LABELS).map(([key, { label, unit }]) => {
              const fact = Number(lastMetric[key] || 0);
              const tgt  = targets ? Number(targets[TARGET_KEYS[key]] || 0) : 0;
              const pct  = tgt > 0 ? Math.min(Math.round((fact / tgt) * 100), 100) : null;
              const color = pct == null ? '#6b7280' : pct >= 100 ? '#16a34a' : pct >= 60 ? '#2563eb' : '#f59e0b';
              return (
                <div key={key} style={{ background:'#fff', borderRadius:10, padding:14, boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize:11, color:'#6b7280', marginBottom:4 }}>{label}</div>
                  <div style={{ fontSize:22, fontWeight:700, color:'#111827', marginBottom:4 }}>{formatNum(fact, unit)}</div>
                  {tgt > 0 && (
                    <>
                      <div style={{ height:5, background:'#e5e7eb', borderRadius:3, marginBottom:4 }}>
                        <div style={{ height:5, width:(pct||0)+'%', background:color, borderRadius:3, transition:'width 0.4s' }} />
                      </div>
                      <div style={{ fontSize:11, color:color, fontWeight:600 }}>
                        {pct}% от цели {formatNum(tgt, unit)}
                      </div>
                    </>
                  )}
                  {!tgt && <div style={{ fontSize:11, color:'#9ca3af' }}>цель не задана</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Форма редактирования целей */}
      {showTargetForm && (
        <div style={{ background:'#fffbeb', border:'1px solid #fcd34d', borderRadius:12, padding:20, marginBottom:24 }}>
          <h3 style={{ margin:'0 0 14px', fontSize:15, fontWeight:700, color:'#92400e' }}>Цели</h3>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(160px, 1fr))', gap:12 }}>
            {[
              { key:'target_leads',     label:'Обращений в месяц' },
              { key:'target_contracts', label:'Договоров в месяц' },
              { key:'target_revenue',   label:'Выручка в месяц, ₽' },
              { key:'target_reviews',   label:'Отзывов на Яндексе' },
              { key:'target_rating',    label:'Рейтинг' },
              { key:'target_reserve',   label:'Резерв, ₽' },
            ].map(({ key, label }) => (
              <div key={key}>
                <div style={{ fontSize:12, color:'#92400e', marginBottom:4 }}>{label}</div>
                <input type="number" step="any" value={targetForm[key]}
                  onChange={e => setTargetForm({ ...targetForm, [key]: e.target.value })}
                  style={{ width:'100%', padding:'6px 8px', border:'1px solid #fcd34d', borderRadius:6, boxSizing:'border-box' }} />
              </div>
            ))}
          </div>
          <div style={{ marginTop:12, display:'flex', gap:8 }}>
            <button onClick={handleTargetSubmit} style={{ background:'#f59e0b', color:'#fff', border:'none', borderRadius:8, padding:'8px 20px', cursor:'pointer', fontWeight:600 }}>Сохранить</button>
            <button onClick={() => setShowTargetForm(false)} style={{ background:'#f3f4f6', color:'#374151', border:'none', borderRadius:8, padding:'8px 20px', cursor:'pointer' }}>Отмена</button>
          </div>
        </div>
      )}

      {/* Дорожная карта */}
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>Дорожная карта</h2>
      <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 16 }}>Клик по задаче — смена статуса</p>
      {phases.map(phase => (
        <PhaseCard key={phase.id} phase={phase} onTaskClick={handleTaskClick} onPhaseDate={handlePhaseDate} onTaskDate={handleRoadmapTaskDate} onRecurringChange={handleRecurringChange} />
      ))}

      {/* Метрики */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '24px 0 12px' }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Метрики по месяцам</h2>
        <button onClick={() => setShowMetricForm(!showMetricForm)} style={{
          background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8,
          padding: '8px 16px', cursor: 'pointer', fontSize: 13
        }}>+ Внести данные</button>
      </div>

      {showMetricForm && (
        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Месяц</div>
              <input type="month" value={metricForm.month ? metricForm.month.slice(0,7) : ''}
                onChange={e => handleMonthChange(e.target.value)}
                style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6 }} />
              {autofillLoading && <div style={{fontSize:11,color:'#6b7280'}}>Загружаю из CRM...</div>}
            </div>
            {Object.entries(METRIC_LABELS).map(([key, { label }]) => (
              <div key={key}>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{label}</div>
                <input type="number" step="any" value={metricForm[key]}
                  onChange={e => setMetricForm({ ...metricForm, [key]: e.target.value })}
                  style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6 }} />
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button onClick={handleMetricSubmit} style={{
              background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8,
              padding: '8px 20px', cursor: 'pointer'
            }}>Сохранить</button>
            <button onClick={() => setShowMetricForm(false)} style={{
              background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8,
              padding: '8px 20px', cursor: 'pointer'
            }}>Отмена</button>
          </div>
        </div>
      )}

      {metrics.length > 0 && (
        <>
          <div style={{ overflowX: 'auto', marginBottom:24 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Месяц</th>
                  {Object.values(METRIC_LABELS).map(({ label }) => (
                    <th key={label} style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>{label}</th>
                  ))}
                  <th style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb' }}></th>
                </tr>
              </thead>
              <tbody>
                {metrics.map(m => (
                  <React.Fragment key={m.id}>
                    <tr style={{ borderBottom: editingMetricId === m.id ? 'none' : '1px solid #f3f4f6', background: editingMetricId === m.id ? '#f0f9ff' : 'transparent' }}>
                      <td style={{ padding: '8px 12px', whiteSpace:'nowrap', fontWeight: 600 }}>
                        {new Date(m.month).toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })}
                      </td>
                      {Object.entries(METRIC_LABELS).map(([key, { unit }]) => (
                        <td key={key} style={{ padding: '8px 12px', textAlign: 'right' }}>{formatNum(m[key], unit)}</td>
                      ))}
                      <td style={{ padding: '8px 12px', whiteSpace:'nowrap', textAlign:'right' }}>
                        <button onClick={() => setEditingMetricId(editingMetricId === m.id ? null : m.id)}
                          style={{ fontSize:11, padding:'2px 8px', borderRadius:5, border:'1px solid #e5e7eb', background: editingMetricId === m.id ? '#dbeafe' : '#f9fafb', cursor:'pointer', color: editingMetricId === m.id ? '#2563eb' : '#6b7280', marginRight:4 }}>
                          {editingMetricId === m.id ? 'Свернуть' : 'Редакт.'}
                        </button>
                        <button onClick={() => deleteMetric(m.id)}
                          style={{ fontSize:11, padding:'2px 8px', borderRadius:5, border:'1px solid #fee2e2', background:'#fff5f5', cursor:'pointer', color:'#dc2626' }}>
                          Удалить
                        </button>
                      </td>
                    </tr>
                    {editingMetricId === m.id && (
                      <tr style={{ background: '#f0f9ff', borderBottom: '1px solid #bfdbfe' }}>
                        <td colSpan={Object.keys(METRIC_LABELS).length + 2} style={{ padding: '12px 12px' }}>
                          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(150px, 1fr))', gap:10 }}>
                            {Object.entries(METRIC_LABELS).map(([key, { label }]) => (
                              <div key={key}>
                                <div style={{ fontSize:11, color:'#6b7280', marginBottom:3 }}>{label}</div>
                                <input type="number" step="any" defaultValue={m[key] ?? ''}
                                  id={`inline-edit-${m.id}-${key}`}
                                  style={{ width:'100%', padding:'5px 8px', border:'1px solid #bfdbfe', borderRadius:6, fontSize:13, boxSizing:'border-box' }} />
                              </div>
                            ))}
                          </div>
                          <div style={{ marginTop:10, display:'flex', gap:8 }}>
                            <button onClick={async () => {
                              const updated = { month: m.month };
                              Object.keys(METRIC_LABELS).forEach(key => {
                                const el = document.getElementById(`inline-edit-${m.id}-${key}`);
                                updated[key] = el ? el.value : m[key];
                              });
                              await api.post('/roadmap/metrics', updated);
                              setEditingMetricId(null);
                              loadAll();
                            }} style={{ background:'#2563eb', color:'#fff', border:'none', borderRadius:7, padding:'7px 18px', cursor:'pointer', fontWeight:600, fontSize:13 }}>
                              Сохранить
                            </button>
                            <button onClick={() => setEditingMetricId(null)}
                              style={{ background:'#f3f4f6', color:'#374151', border:'none', borderRadius:7, padding:'7px 18px', cursor:'pointer', fontSize:13 }}>
                              Отмена
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* График динамики */}
          <h2 style={{ fontSize:18, fontWeight:700, marginBottom:16 }}>Динамика</h2>
          <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, padding:20, marginBottom:16 }}>
            <div style={{ fontSize:13, color:'#6b7280', marginBottom:12 }}>Новые клиенты и выручка по месяцам</div>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={metrics.map(m => ({
                name: new Date(m.month).toLocaleDateString('ru-RU', { month:'short', year:'2-digit' }),
                'Клиенты': Number(m.new_clients || 0),
                'Выручка (тыс)': Math.round(Number(m.revenue || 0) / 1000),
              }))}>
                <XAxis dataKey="name" tick={{ fontSize:11 }} />
                <YAxis yAxisId="left" tick={{ fontSize:11 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize:11 }} />
                <Tooltip formatter={(val, name) => name === 'Выручка (тыс)' ? val + ' тыс ₽' : val} />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="Клиенты" stroke="#2563eb" strokeWidth={2} dot={{ r:4 }} />
                <Line yAxisId="right" type="monotone" dataKey="Выручка (тыс)" stroke="#10b981" strokeWidth={2} dot={{ r:4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}
